import {useMemo} from 'react';
import {z} from 'zod';

import {useStores} from '../../../../stores/use-stores';

const getCurrentTwoDigitYear = () => parseInt(String(new Date().getFullYear()).slice(-2), 10);
const getCurrentMonth = () => new Date().getMonth() + 1; // 1-12

export const CardTokeniserTillSchema = z
  .object({
    cardHolder: z.string().min(1, "Cardholder's name is required"),
    // number/cvv store 'valid' string when valid, null when invalid
    // Till iframes handle actual card validation, we just track the state
    number: z
      .string()
      .nullable()
      .refine(val => val !== null && val.length > 0, {message: 'Valid card number is required'}),
    cvv: z
      .string()
      .nullable()
      .refine(val => val !== null && val.length > 0, {message: 'Valid CVV is required'}),
    month: z
      .string()
      .min(1, 'Expiry month is required')
      .refine(val => /^\d{1,2}$/.test(val), {message: 'Enter month (1-12)'})
      .refine(
        val => {
          const num = parseInt(val, 10);
          return num >= 1 && num <= 12;
        },
        {message: 'Month must be 1-12'},
      ),
    year: z
      .string()
      .min(1, 'Expiry year is required')
      .refine(val => /^\d{2}$/.test(val), {message: 'Enter 2-digit year (YY)'})
      .refine(
        val => {
          const num = parseInt(val, 10);
          const currentYear = getCurrentTwoDigitYear();
          // Allow current year through next 20 years
          return num >= currentYear && num <= currentYear + 20;
        },
        {message: 'Year is expired or invalid'},
      ),
  })
  .superRefine((data, ctx) => {
    // Cross-field validation: if year is current year, month must be >= current month
    const month = parseInt(data.month, 10);
    const year = parseInt(data.year, 10);
    const currentYear = getCurrentTwoDigitYear();
    const currentMonth = getCurrentMonth();

    if (!isNaN(month) && !isNaN(year) && year === currentYear && month < currentMonth) {
      ctx.addIssue({
        code: 'custom',
        message: 'Card is expired',
        path: ['month'],
      });
    }
  });

export type CardTokeniserTillValues = z.infer<typeof CardTokeniserTillSchema>;

export const useCardTokeniserTillSchemaDefaultValues = () => {
  const {userStore} = useStores();
  const {isLoggedIn} = userStore;

  const cardholder = useMemo(
    () => (isLoggedIn ? `${userStore.firstName} ${userStore.lastName}` : ''),
    [isLoggedIn, userStore.firstName, userStore.lastName],
  );

  const defaultValues: CardTokeniserTillValues = {
    cardHolder: cardholder,
    number: null,
    cvv: null,
    month: '',
    year: '',
  };

  return defaultValues;
};
